import { useMemo, useState } from 'react';
import { DefaultDesignBar } from '@components/DefaultDesignBar';
import { usePaginate } from '@hooks/usePaginate';
import { ModelEditDialog } from '@components/dialogs/ModelEditDialog';
import { ModelTable } from '@components/tables/ModelTable';
import { OrangeButton } from '@components/buttons/OrangeButton';
import { useLoadDesignData } from '@hooks/useLoadDesignData';
import { optimisticModelUpdate, keyBy } from '@utils/data';

import type { Model } from '@features/redux/slices/designSlice';

const ITEMS_PER_PAGE = 10;

export const ModelsPage = () => {
    const { models, manufacturers, categories, loadingModel } = useLoadDesignData();

    const manufacturerMap = useMemo(() => keyBy(manufacturers, 'manufacturerId'), [manufacturers]);
    const categoryMap = useMemo(() => keyBy(categories, 'categoryId'), [categories]);

    const [editOpen, setEditOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState<Model>();
    const [pageNumber, setPageNumber] = useState(1);

    const [searchText, setSearchText] = useState('');

    const modelFilter = useMemo(
        () => (model: Model, searchText: string) => {
            const searchLower = searchText.toLowerCase();
            const modelLower = model.model.toLowerCase();
            const manufacturer = manufacturerMap[model.manufacturerId];
            const manufacturerLower = manufacturer?.manufacturerName.toLowerCase() || '';
            return modelLower.includes(searchLower) || manufacturerLower.includes(searchLower);
        },
        [manufacturerMap]
    );

    const [pagedItems, pageCount] = usePaginate({
        items: models,
        filter: modelFilter,
        searchText,
        pageNumber,
        itemsPerPage: ITEMS_PER_PAGE,
    });

    const search = (searchText: string) => {
        setSearchText(searchText);
        setPageNumber(1);
    };

    const selectPage = (page: number) => {
        setPageNumber(page);
    };

    const openEditDialog = (model: Model) => {
        setEditOpen(true);
        setSelectedModel(model);
    };

    const closeDialog = (newModel?: Model) => {
        setEditOpen(false);
        setSelectedModel(undefined);

        if (newModel) {
            optimisticModelUpdate(models, newModel);
        }
    };

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <DefaultDesignBar selectedRoute={'models'} onSearch={search}>
                <OrangeButton onClick={() => setEditOpen(true)}>+ Model</OrangeButton>
            </DefaultDesignBar>

            <ModelTable
                selectModel={openEditDialog}
                selectPage={selectPage}
                loadingModel={loadingModel}
                pageCount={pageCount}
                pageNumber={pageNumber}
                models={pagedItems}
                manufacturerMap={manufacturerMap}
            />

            {editOpen && (
                <ModelEditDialog
                    model={selectedModel}
                    manufacturerMap={manufacturerMap}
                    categoryMap={categoryMap}
                    cancel={closeDialog}
                />
            )}
        </div>
    );
};
